import React, { useCallback, useEffect, useState } from "react";
import { Loader2, Mail, RefreshCw, RotateCcw, Send } from "lucide-react";
import { toast } from "sonner";
import { epostaMerkez } from "@/lib/api";

const DURUM_STYLE = {
    BEKLIYOR: "text-amber-300 bg-amber-500/10 border-amber-500/30",
    GONDERILIYOR: "text-sky-300 bg-sky-500/10 border-sky-500/30",
    GONDERILDI: "text-emerald-300 bg-emerald-500/10 border-emerald-500/30",
    HATA: "text-rose-300 bg-rose-500/10 border-rose-500/30",
};

export default function EpostaGidenPanel() {
    const [ozet, setOzet] = useState(null);
    const [items, setItems] = useState([]);
    const [sablonlar, setSablonlar] = useState([]);
    const [filter, setFilter] = useState("all");
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState({ konu: "", icerik: "", manuel_email: "", sablon_id: "" });
    const [sablonForm, setSablonForm] = useState({ ad: "", konu: "", icerik: "" });
    const [subTab, setSubTab] = useState("kuyruk");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [o, list, sab] = await Promise.all([
                epostaMerkez.gidenOzet(),
                epostaMerkez.gidenList(filter === "all" ? {} : { durum: filter }),
                epostaMerkez.sablonList(),
            ]);
            setOzet(o);
            setItems(list);
            setSablonlar(sab);
        } catch {
            toast.error("Giden e-posta verisi yüklenemedi.");
        } finally {
            setLoading(false);
        }
    }, [filter]);

    useEffect(() => {
        load();
    }, [load]);

    async function handleGonder(e) {
        e.preventDefault();
        if (!form.konu.trim() || !form.icerik.trim()) {
            toast.error("Konu ve içerik gerekli.");
            return;
        }
        try {
            const payload = {
                konu: form.konu,
                icerik: form.icerik,
                manuel_email: form.manuel_email || undefined,
                sablon: form.sablon_id ? Number(form.sablon_id) : undefined,
            };
            await epostaMerkez.gidenOlustur(payload);
            toast.success("Kuyruğa eklendi ve gönderim başlatıldı.");
            setForm({ konu: "", icerik: "", manuel_email: "", sablon_id: "" });
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Gönderilemedi.");
        }
    }

    async function handleSablon(e) {
        e.preventDefault();
        try {
            await epostaMerkez.sablonOlustur(sablonForm);
            toast.success("Şablon oluşturuldu.");
            setSablonForm({ ad: "", konu: "", icerik: "" });
            load();
        } catch {
            toast.error("Şablon kaydedilemedi.");
        }
    }

    async function handleRetry(id) {
        try {
            const res = await epostaMerkez.gidenRetry(id);
            if (res.ok) toast.success("Yeniden gönderildi.");
            else toast.error(res.hata_mesaji || res.detail || "Hata");
            load();
        } catch {
            toast.error("Yeniden deneme başarısız.");
        }
    }

    function applySablon(id) {
        const s = sablonlar.find((x) => x.id === Number(id));
        if (s) setForm((f) => ({ ...f, konu: s.konu || f.konu, icerik: s.icerik || f.icerik, sablon_id: id }));
    }

    return (
        <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {[
                    ["Bekleyen", ozet?.bekleyen, "text-amber-300"],
                    ["Gönderildi", ozet?.gonderildi, "text-emerald-300"],
                    ["Hata", ozet?.hata, "text-rose-300"],
                    ["Toplam", ozet?.toplam, "text-slate-200"],
                    ["Şablon", ozet?.sablon_sayisi, "text-sky-300"],
                ].map(([label, val, color]) => (
                    <div key={label} className="rounded-xl border border-white/10 bg-[#121A2F]/80 p-3">
                        <div className="text-[10px] uppercase text-slate-500">{label}</div>
                        <div className={`text-xl font-bold mt-1 ${color}`}>{val ?? "—"}</div>
                    </div>
                ))}
            </div>

            <div className="flex gap-2 border-b border-white/10 pb-2">
                {[
                    { id: "kuyruk", label: "Kuyruk" },
                    { id: "yeni", label: "Yeni gönder" },
                    { id: "sablonlar", label: "Şablonlar" },
                ].map((t) => (
                    <button
                        key={t.id}
                        type="button"
                        onClick={() => setSubTab(t.id)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${
                            subTab === t.id ? "bg-sky-500/15 text-sky-200 border border-sky-500/30" : "text-slate-500"
                        }`}
                    >
                        {t.label}
                    </button>
                ))}
                <button type="button" onClick={load} className="ml-auto text-xs text-slate-400 inline-flex items-center gap-1">
                    <RefreshCw className="h-3.5 w-3.5" /> Yenile
                </button>
            </div>

            {subTab === "kuyruk" && (
                <div className="space-y-3">
                    <div className="flex flex-wrap gap-2">
                        {[
                            { k: "all", l: "Tümü" },
                            { k: "BEKLIYOR", l: "Bekleyen" },
                            { k: "GONDERILDI", l: "Gönderildi" },
                            { k: "HATA", l: "Hata" },
                        ].map((f) => (
                            <button
                                key={f.k}
                                type="button"
                                onClick={() => setFilter(f.k)}
                                className={`px-2 py-1 rounded-full text-[10px] border ${
                                    filter === f.k ? "border-sky-500/40 text-sky-200" : "border-white/10 text-slate-500"
                                }`}
                            >
                                {f.l}
                            </button>
                        ))}
                    </div>
                    {loading ? (
                        <Loader2 className="h-6 w-6 animate-spin text-slate-500 mx-auto" />
                    ) : (
                        <div className="space-y-2 max-h-[480px] overflow-y-auto">
                            {items.length === 0 ? (
                                <p className="text-xs text-slate-500 text-center py-8">Kayıt yok</p>
                            ) : (
                                items.map((row) => (
                                    <div
                                        key={row.id}
                                        className="rounded-lg border border-white/10 bg-[#0B0F19]/60 p-3 text-xs"
                                    >
                                        <div className="flex justify-between gap-2">
                                            <span className="text-slate-200 font-medium truncate">{row.konu}</span>
                                            <span
                                                className={`px-2 py-0.5 rounded border text-[10px] shrink-0 ${
                                                    DURUM_STYLE[row.durum] || DURUM_STYLE.BEKLIYOR
                                                }`}
                                            >
                                                {row.durum || (row.gonderildi_mi ? "GONDERILDI" : "BEKLIYOR")}
                                            </span>
                                        </div>
                                        <p className="text-slate-500 mt-1">
                                            {row.manuel_email || row.muvekkil || "—"} · deneme {row.deneme_sayisi || 0}
                                        </p>
                                        {row.hata_mesaji && (
                                            <p className="text-rose-300/90 mt-1 line-clamp-2">{row.hata_mesaji}</p>
                                        )}
                                        {row.durum === "HATA" && (
                                            <button
                                                type="button"
                                                onClick={() => handleRetry(row.id)}
                                                className="mt-2 h-7 px-2 rounded bg-rose-600/80 text-white text-[10px] inline-flex items-center gap-1"
                                            >
                                                <RotateCcw className="h-3 w-3" /> Yeniden dene
                                            </button>
                                        )}
                                    </div>
                                ))
                            )}
                        </div>
                    )}
                </div>
            )}

            {subTab === "yeni" && (
                <form onSubmit={handleGonder} className="space-y-3 rounded-xl border border-white/10 p-4">
                    <select
                        value={form.sablon_id}
                        onChange={(e) => applySablon(e.target.value)}
                        className="w-full h-9 rounded border border-white/10 bg-black/30 px-2 text-xs"
                    >
                        <option value="">Şablon seç (opsiyonel)</option>
                        {sablonlar.map((s) => (
                            <option key={s.id} value={s.id}>{s.ad}</option>
                        ))}
                    </select>
                    <input
                        placeholder="Alıcı e-posta"
                        value={form.manuel_email}
                        onChange={(e) => setForm({ ...form, manuel_email: e.target.value })}
                        className="w-full h-9 rounded border border-white/10 bg-black/30 px-3 text-xs"
                    />
                    <input
                        placeholder="Konu"
                        value={form.konu}
                        onChange={(e) => setForm({ ...form, konu: e.target.value })}
                        className="w-full h-9 rounded border border-white/10 bg-black/30 px-3 text-xs"
                        required
                    />
                    <textarea
                        placeholder="HTML içerik"
                        value={form.icerik}
                        onChange={(e) => setForm({ ...form, icerik: e.target.value })}
                        rows={8}
                        className="w-full rounded border border-white/10 bg-black/30 p-3 text-xs font-mono"
                        required
                    />
                    <button type="submit" className="h-9 px-4 rounded bg-sky-600 text-white text-xs font-semibold inline-flex items-center gap-1">
                        <Send className="h-3.5 w-3.5" /> Gönder
                    </button>
                </form>
            )}

            {subTab === "sablonlar" && (
                <div className="grid lg:grid-cols-2 gap-4">
                    <form onSubmit={handleSablon} className="space-y-2 rounded-xl border border-white/10 p-4">
                        <h4 className="text-xs font-semibold text-slate-300">Yeni şablon</h4>
                        <input
                            placeholder="Şablon adı"
                            value={sablonForm.ad}
                            onChange={(e) => setSablonForm({ ...sablonForm, ad: e.target.value })}
                            className="w-full h-9 rounded border border-white/10 bg-black/30 px-3 text-xs"
                            required
                        />
                        <input
                            placeholder="Konu"
                            value={sablonForm.konu}
                            onChange={(e) => setSablonForm({ ...sablonForm, konu: e.target.value })}
                            className="w-full h-9 rounded border border-white/10 bg-black/30 px-3 text-xs"
                            required
                        />
                        <textarea
                            placeholder="HTML"
                            value={sablonForm.icerik}
                            onChange={(e) => setSablonForm({ ...sablonForm, icerik: e.target.value })}
                            rows={6}
                            className="w-full rounded border border-white/10 bg-black/30 p-2 text-xs font-mono"
                            required
                        />
                        <button type="submit" className="h-8 px-3 rounded bg-emerald-600 text-white text-xs">Kaydet</button>
                    </form>
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                        {sablonlar.map((s) => (
                            <div key={s.id} className="rounded border border-white/10 p-3 text-xs">
                                <div className="font-medium text-slate-200">{s.ad}</div>
                                <div className="text-slate-500">{s.konu}</div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
